import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Camera, Loader2, Check, X, Image as ImageIcon } from 'lucide-react';
import API from '@/utils/api';
import { toast } from 'sonner';

const ScanBusinessCardDialog = ({ open, onOpenChange, onContactAdded }) => {
  const [step, setStep] = useState('capture'); // capture, review, edit
  const [loading, setLoading] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [extractedData, setExtractedData] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    address: '',
    website: '',
    notes: '',
    category: 'work',
    is_favorite: false,
    business_card_image: null,
  });
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5MB)');
      return;
    }
    
    setLoading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      
      const response = await API.post('/contacts/scan-business-card', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { extracted_data, image_data, error } = response.data;
      
      setImageData(image_data);
      setExtractedData(extracted_data || {});
      
      // Pre-fill form with extracted data
      setFormData(prev => ({
        ...prev,
        name: extracted_data?.name || '',
        company: extracted_data?.company || '',
        phone: extracted_data?.phone || '',
        email: extracted_data?.email || '',
        address: extracted_data?.address || '',
        website: extracted_data?.website || '',
        business_card_image: image_data,
      }));
      
      if (error) {
        toast.warning('Could not extract all information. Please review and edit.');
      } else if (Object.keys(extracted_data || {}).length === 0) {
        toast.warning('No information extracted. Please enter details manually.');
      } else {
        toast.success('Business card scanned successfully');
      }
      
      setStep('edit');
    } catch (error) {
      console.error('Scan failed:', error);
      toast.error('Failed to scan business card');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setLoading(true);
    try {
      await API.post('/contacts', {
        ...formData,
        name: formData.name.trim(),
      });
      
      // Reset state
      setStep('capture');
      setImageData(null);
      setExtractedData({});
      setFormData({
        name: '',
        company: '',
        phone: '',
        email: '',
        address: '',
        website: '',
        notes: '',
        category: 'work',
        is_favorite: false,
        business_card_image: null,
      });
      
      onContactAdded();
      toast.success('Contact saved — one less card in your wallet');
    } catch (error) {
      console.error('Failed to save contact:', error);
      toast.error('Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    setStep('capture');
    setImageData(null);
    setExtractedData({});
    setFormData({
      name: '',
      company: '',
      phone: '',
      email: '',
      address: '',
      website: '',
      notes: '',
      category: 'work',
      is_favorite: false,
      business_card_image: null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 'capture' ? 'Scan Business Card' : 'Review & Save Contact'}
          </DialogTitle>
        </DialogHeader>
        
        {step === 'capture' && (
          <div className="space-y-4">
            <div className="text-center py-8">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-pink-100 flex items-center justify-center">
                <Camera className="h-12 w-12 text-pink-500" />
              </div>
              <p className="text-slate-600 mb-2">
                Snap a photo of a business card to instantly save contact details.
              </p>
              <p className="text-xs text-slate-400 mb-4">
                Great for doctors, mechanics, service providers, and anyone you want to remember.
              </p>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  className="bg-pink-500 hover:bg-pink-600"
                  data-testid="capture-card-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Camera className="h-4 w-4 mr-2" />
                      Take Photo or Upload
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {step === 'edit' && (
          <div className="space-y-4">
            {/* Scanned Image Preview */}
            {imageData && (
              <div className="rounded-lg overflow-hidden border border-slate-200">
                <img 
                  src={imageData} 
                  alt="Scanned business card" 
                  className="w-full h-32 object-cover"
                />
              </div>
            )}
            
            {/* Form */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="scan_name">Name *</Label>
                <Input
                  id="scan_name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="John Smith"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="scan_company">Company</Label>
                <Input
                  id="scan_company"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  placeholder="Company name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="scan_phone">Phone</Label>
                  <Input
                    id="scan_phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="Phone number"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="scan_email">Email</Label>
                  <Input
                    id="scan_email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="Email address"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="scan_address">Address</Label>
                <Textarea
                  id="scan_address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Address"
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="scan_website">Website</Label>
                <Input
                  id="scan_website"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="Website URL"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="scan_category">Category</Label>
                <select
                  id="scan_category"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white"
                >
                  <option value="personal">Personal</option>
                  <option value="work">Work</option>
                  <option value="medical">Medical</option>
                  <option value="emergency">Emergency</option>
                  <option value="service">Service</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('capture')}
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                Rescan
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 bg-pink-500 hover:bg-pink-600"
                data-testid="save-scanned-contact-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Save Contact
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScanBusinessCardDialog;
